import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  StyleSheet,
  ScrollView,
  View,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import * as Location from 'expo-location';

import TripSearchWidget from '../components/TripSearchWidget/TripSearchWidget';
import ProfileHeader from '../components/ProfileHeader';
import {
  setSearchInput,
  tripSearch,
  setGeolocation,
  setLocationDetail,
} from '../store/actions/search';
import { getAddressFromCoords, getNearestAirportFromCoords } from '../services/api/search.service';
import { openAlert } from '../store/actions/alert';
import { validateTripSearch } from '../utils/search.utils';
import { selectMytrips, selectMytripsCount } from '../store/selectors/mytrips.selector';
import ExpenseWidget from '../components/ExpenseWidget/ExpenseWidget';
import { Color, CommonProperties, CommonStyles } from '../styles';
import { selectExpenseList } from '../store/selectors/expense.selector';
import { isExpenseAllowed, selectUserProfileImage } from '../store/selectors/profile.selector';
import FloatButton from '../components/FloatButton';
import { AA_ENABLED, TRIPEVENTS_ENABLED } from '../config/env';
import EventWidget from '../components/EventWidget/EventWidget';
import { scale, scaleVertically } from '../utils/scale.utils';
import { useTranslation } from 'react-i18next';
import { i18nNS } from '../i18n';
import { clearSelectedCarCache } from '../store/actions/car';
import { selectedCarComponents } from '../store/selectors/car.selector';
import { clearSelectedAirCache } from '../store/actions/air';
import { clearSeatSelected } from '../store/actions/seat';
import { clearHotel } from '../store/actions/hotel';
import { getCreditCardsList, setExpenseResults } from '../store/actions/expense';
import { appType, expenseActions, Source } from '../constants/Enums';
import { isNullOrEmpty } from '../utils/common.utils';
import BaseText from '../components/BaseText';
import useChargeBottomSheetActions from '../utils/useChargeBottomSheetActions';

const HomeScreen = () => {
  const { t } = useTranslation(i18nNS.common);
  const myTripsCount = useSelector(selectMytripsCount);
  const isExpense = useSelector(isExpenseAllowed);
  const expenses = useSelector(selectExpenseList);
  const selectedCar = useSelector(selectedCarComponents);
  const dispatch = useDispatch();
  const searchTripLoading = useSelector((s) => s.search.tripResultsLoading);
  const user = useSelector((state) => state && state.auth && state.auth.user);
  const userImage = useSelector(selectUserProfileImage);
  const [type, setType] = useState(0);
  const [shownEventUpload, setShowEventUpload] = useState(false);
  const selectedAir = useSelector((state) => state?.air?.components);
  const [expenseChargesRef, chargeSheetActions, ChargeBottomSheet] = useChargeBottomSheetActions();

  const onSearchHandler = async (searchInput, searchInputState) => {
    const validationResult = validateTripSearch(searchInput);
    if (validationResult.isFailed) {
      dispatch(openAlert({ message: validationResult.msg }));
    } else {
      await dispatch(clearSelectedCarCache(searchInput, selectedCar));
      dispatch(clearSelectedAirCache(selectedAir));
      dispatch(clearSeatSelected());
      dispatch(clearHotel());
      await dispatch(setSearchInput(searchInput, searchInputState));
      dispatch(tripSearch());
    }
  };

  const onEventWidgetChange = useCallback(() => {
    setShowEventUpload(false);
  }, []);

  useEffect(() => {
    async function getLocationAsync() {
      try {
        const obj = await Location.requestForegroundPermissionsAsync();
        if (obj?.status === 'granted') {
          const location = await Location.getCurrentPositionAsync({});
          const latitude = location.coords.latitude;
          const longitude = location.coords.longitude;
          const locationRequest = {
            Latitude: latitude,
            Longitude: longitude,
          };
          const airport = await getNearestAirportFromCoords(locationRequest);
          const address = await getAddressFromCoords(`${latitude},${longitude}`);
          if (address) {
            const addressObj = {
              label: address.results[0].formatted_address,
              placeId: address.results[0].place_id,
              category: 'G',
              regionid: 0,
              key: 0,
            };
            dispatch(setLocationDetail(addressObj));
          }
          dispatch(setGeolocation(latitude, longitude, airport?.Code));
        }
      } catch (ex) {
        __DEV__ && console.info('get location failed', ex);
      }
    }
    getLocationAsync();
  }, [dispatch]);
  useEffect(() => {
    async function getRes() {
      const actionType = {
        ActionCode: expenseActions.GET_REPORTS_AND_EXPENSES,
      };

      dispatch(getCreditCardsList());
      await dispatch(setExpenseResults(actionType));
    }
    getRes();
  }, []);
  function getNoOfExpenses() {
    if (isNullOrEmpty(expenses)) {
      return 0;
    }
    const parsedExpenses = JSON.parse(expenses);
    if (isNullOrEmpty(parsedExpenses)) {
      return 0;
    }
    return parsedExpenses?.length || 0;
  }

  const handleTypePress = (type) => {
    setType(type);
  };

  return (
    <KeyboardAvoidingView
      style={CommonStyles.flex1}
      behavior={Platform.OS === Source.IOS ? 'padding' : ''}
      keyboardVerticalOffset={Platform.OS === Source.IOS ? 100 : 0}
    >
      <View style={CommonStyles.flex1}>
        <ScrollView contentContainerStyle={styles.scrollViewContainer} nestedScrollEnabled={true}>
          {isExpense || TRIPEVENTS_ENABLED ? (
            <View style={styles.switchWrap}>
              <TouchableOpacity onPress={() => handleTypePress(0)}>
                <BaseText style={type === appType.TRAVEL ? styles.selectedText : styles.textStyle}>
                  {t('Travel')}
                </BaseText>
              </TouchableOpacity>
              {/* <TouchableOpacity onPress={() => handleTypePress(2)}>
              <BaseText style={type === appType.EVENT ? styles.selectedText : styles.textStyle}>
                {t('Event')}
              </BaseText>
            </TouchableOpacity> */}
              <TouchableOpacity onPress={() => handleTypePress(1)}>
                <BaseText style={type === appType.EXPENSE ? styles.selectedText : styles.textStyle}>
                  {t('Expense')}
                </BaseText>
              </TouchableOpacity>
            </View>
          ) : null}
          {type === appType.TRAVEL ? (
            <TripSearchWidget onSearch={onSearchHandler} loading={searchTripLoading} />
          ) : null}
          {type === appType.EXPENSE ? (
            isExpense ? (
              <ExpenseWidget
                chargeSheetActions={chargeSheetActions}
                expenseChargesRef={expenseChargesRef}
              />
            ) : (
              <EventWidget openUploadImage={shownEventUpload} onChange={onEventWidgetChange} />
            )
          ) : null}
        </ScrollView>
        {!isExpense && TRIPEVENTS_ENABLED && type === 1 ? (
          <FloatButton
            size={scale(60)}
            onPress={() => setShowEventUpload(true)}
            iconName="image"
            iconSize={36}
            iconStyle={styles.iconStyle}
            style={styles.float}
          />
        ) : null}
      </View>
      {/* Using ChargeBottomSheet as an element to preserve transition effects */}
      <View>{ChargeBottomSheet}</View>
    </KeyboardAvoidingView>
  );
};

export const styles = StyleSheet.create({
  scrollViewContainer: {
    flexGrow: 1,
    backgroundColor: Color.background,
  },
  switchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    ...CommonProperties.mb5,
    ...CommonProperties.justifyCenter,
  },
  textStyle: {
    ...CommonProperties.h3,
    width: scale(100),
    textAlign: 'center',
    color: Color.primary,
  },
  selectedText: {
    ...CommonProperties.h2,
    width: scale(100),
    textAlign: 'center',
    color: Color.active,
    borderBottomColor: Color.active,
    borderBottomWidth: 2,
  },
  iconStyle: {
    color: Color.accent,
  },
  float: {
    backgroundColor: Color.primary,
  },
  bottomSheetBaseText: {
    fontSize: 20,
    marginHorizontal: 'auto',
    marginBottom: scaleVertically(10),
  },
  bottomSheetView: { gap: 10, marginBottom: 10 },
});

export default HomeScreen;
